// Sprint Auto-update F6 — Helper de upload binary client-side.
//
// Flow:
//   1. Calcula SHA-256 do file via Web Crypto API (crypto.subtle.digest)
//   2. Pede presigned PUT URL ao backend (/admin/client-builds/upload-url)
//   3. PUT direto R2 com binary body via XMLHttpRequest (progress callback)
//   4. Caller faz POST /admin/client-builds com r2_object_key + text_hash_sha256
//
// Por que XMLHttpRequest vs axios: axios não tem upload progress facil.
// XHR é canônico pra progress em browsers modernos.

import type { Channel } from './releases';
import { generateUploadUrl, type UploadUrlResponse } from './clientBuilds';

/// Calcula SHA-256 hex lowercase de um File via Web Crypto API.
/// Performance: ~100MB/s em browsers modernos (~5s pra binary 500MB).
export async function computeSha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hashBuf = await crypto.subtle.digest('SHA-256', buf);
  const bytes = new Uint8Array(hashBuf);
  return Array.from(bytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export interface UploadOptions {
  name: string;            // build name (AIKA)
  version: string;         // semver (2.1.0)
  channel?: Channel;       // default stable
  file: File;              // binary picked from <input type="file">
  onProgress?: (loaded: number, total: number) => void;
}

export interface UploadResult {
  r2_object_key: string;
  text_hash_sha256: string;
  size_bytes: number;
  expires_at: string;
}

/// Faz upload completo: SHA-256 + presigned URL + PUT R2.
/// Retorna metadata pra caller fazer POST /admin/client-builds.
export async function uploadBinary(opts: UploadOptions): Promise<UploadResult> {
  // 1. SHA-256 client-side
  const sha256 = await computeSha256Hex(opts.file);

  // 2. Pede presigned PUT URL
  const urlResp: UploadUrlResponse = await generateUploadUrl({
    name: opts.name,
    version: opts.version,
    channel: opts.channel,
    filename: opts.file.name,
  });

  // 3. PUT direto R2 via XHR (progress)
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', urlResp.presigned_put_url, true);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) {
        opts.onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`R2 PUT failed: HTTP ${xhr.status} ${xhr.responseText.slice(0, 200)}`));
      }
    };
    xhr.onerror = () => reject(new Error('R2 PUT network error'));
    xhr.send(opts.file);
  });

  return {
    r2_object_key: urlResp.r2_object_key,
    text_hash_sha256: sha256,
    size_bytes: opts.file.size,
    expires_at: urlResp.expires_at,
  };
}
